import { useCallback } from "react";
import classNames from "classnames";
import { isFailed, isRunning, State, Step } from "~/lib/pipeline";
import { StepLinkHighlight } from "~/components/build/Show/routes/list/-components/TreeNode/StepLink";
import { StepItem } from "~/components/build/Show/routes/list/-components/TreeNode/StepItem";
import {
  CollapseChevron,
  useCollapseToggle,
} from "~/components/build/Show/routes/list/-components/TreeNode/CollapseChevron";
import { CollapsibleState } from "~/components/build/Show/routes/list/-components/TreeItem/CollapsibleState";
import useQueryParams from "~/components/build/Show/lib/useQueryParams";

interface Props {
  id?: string;
  step: Step;
  collapsibleState?: CollapsibleState;
  forcedCollapsibleState?: CollapsibleState;
  depth?: number;
  isLoading?: boolean;
  isLoaded?: boolean;
}

export function CollapsibleStepItem({
  id,
  step,
  depth = 0,
  collapsibleState = CollapsibleState.Collapsed,
  forcedCollapsibleState,
  isLoading = false,
  isLoaded,
}: Props) {
  const params = useQueryParams();
  const active = params.sid === step.uuid;
  const toggleable = forcedCollapsibleState === undefined;

  // Use the node ID (which may be namespaced, e.g. for state-scoped groups)
  // to store collapsible state, falling back to step.uuid.
  const nodeId = id ?? step.uuid;

  const { isExpanded: stateExpanded, toggle } = useCollapseToggle(nodeId, collapsibleState);
  // A forced (non-toggleable) node renders as permanently expanded.
  const isExpanded = !toggleable || stateExpanded;

  let highlight: StepLinkHighlight | undefined;
  if (!isExpanded) {
    if (isFailed(step)) {
      highlight = "failed";
    } else if (isRunning(step)) {
      highlight = "running";
    }
  }

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === " ") {
        event.preventDefault();
        toggle();
      }
    },
    [toggle],
  );

  return (
    <StepItem
      step={step}
      active={active}
      depth={depth}
      isLoaded={isLoaded}
      onKeyDown={toggleable ? handleKeyDown : undefined}
      highlight={highlight}
      className={classNames({
        "animate-pulse": isLoading,
      })}
      arrow={
        toggleable ? (
          <CollapseChevron
            nodeId={nodeId}
            collapsibleState={collapsibleState}
            data-testid="collapsible-step-toggle"
            className={classNames({
              "opacity-45 hover:opacity-100":
                step.state === State.WaitingForDependencies && !active,
            })}
          />
        ) : undefined
      }
    />
  );
}
